// src/lib/linkedShareApi.js
// API-calls voor Linked Sharing (koppel vrienden aan je pakket)
// Endpoints (server.js):
//  - GET  /api/me/linked-shares
//  - POST /api/linked-shares            body: { guestUserId, providerId }
//  - POST /api/linked-shares/:id/revoke

function resolveApiBase() {
  // 1) Dev: localhost → lokale backend
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost"
  ) {
    return "http://localhost:5000";
  }

  // 2) Productie: bondapp.tv → productie backend
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "bondapp.tv"
  ) {
    return "https://signal.bondapp.tv";
  }

  // 3) Optioneel: env override (staging/andere omgevingen)
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE;
  }

  // 4) Fallback
  return "http://localhost:5000";
}

const API_BASE = resolveApiBase();

async function request(path, { method = "GET", body, token } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${API_BASE}${path}`;
  let res;

  try {
    res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // mag blijven staan (helpt bij sommige setups), maar token is leidend
      credentials: "include",
    });
  } catch (err) {
    console.error("[linkedShareApi] NETWORK_FAIL", { url, err });
    const e = new Error("NETWORK_FAIL");
    e.cause = err;
    throw e;
  }

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { _raw: text };
  }

  if (!res.ok || data?.ok === false) {
    const code =
      data?.error ||
      (res.status === 401 ? "UNAUTHENTICATED" : "REQUEST_FAILED");

    console.error("[linkedShareApi] HTTP_FAIL", {
      url,
      status: res.status,
      statusText: res.statusText,
      data,
    });

    const err = new Error(code);
    err.code = code;
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return data;
}


/**
 * Haal linked shares op voor current user:
 * - owned: jij deelt uit → bevat guest
 * - received: jij ontvangt → bevat owner
 *
 * Response shape (server):
 * { ok:true, owned:[...], received:[...] }
 */
export async function getMyLinkedShares(token) {
  const data = await request("/api/me/linked-shares", { token });
  return {
    owned: data.owned || [],
    received: data.received || [],
  };
}

/**
 * Maak (of re-activeer) een linked share.
 * Alleen sports / all_access toegestaan door backend.
 *
 * @param {number|string} guestUserId
 * @param {"sports"|"all_access"} providerId
 */
export async function createLinkedShare(guestUserId, providerId, token) {
  const data = await request("/api/linked-shares", {
    method: "POST",
    token,
    body: {
      guestUserId: Number(guestUserId),
      providerId: String(providerId || "").toLowerCase(),
    },
  });

  return data.share;
}

/**
 * Revoke een linked share (alleen owner mag dit).
 * @param {number|string} shareId
 */
export async function revokeLinkedShare(shareId, token) {
  const id = Number(shareId);
  if (!id) {
    const err = new Error("INVALID_ID");
    err.code = "INVALID_ID";
    throw err;
  }

  const data = await request(`/api/linked-shares/${id}/revoke`, {
    method: "POST",
    token,
  });

  return data.share; // { id, revokedAt }
}
